import type RootStore from "stores/RootStore";
import { sameId } from "./dataMapper.types";

/**
 * The Data Mapper's per-tab realtime events, on any of its services.
 *
 * The API emits a background job's progress (explorer profiling and analysis,
 * a .BAK restore, an import) as a custom event ON THE SERVICE that started it,
 * routed to the requesting person's own channel only
 * (packages/api/src/services/data-mapper/data-mapper-events.ts and
 * data-plane/imports.ts). Every socket that person has open
 * hears every one of their tabs' events, so each payload carries the `tabId`
 * the job was started with, and a tab keeps only its own. This helper is that
 * filter, written once: generic over the service path and the event names, so
 * every Data Mapper screen with a background job subscribes the same way.
 *
 * It listens through the raw Feathers client (`rootStore.app.service(path)`),
 * which shares the app's one authenticated socket and re-emits
 * `"<path> <event>"` wire events as `service.on(event)`; nothing is cached.
 */

/** A per-tab event payload: at least the tab id; the rest is the event's own. */
export interface DataMapperTabEventPayload {
  tabId?: unknown;
  [key: string]: unknown;
}

/** The slice of a raw Feathers client service this helper needs. */
interface EventSource {
  on: (event: string, listener: (payload: unknown) => void) => unknown;
  removeListener?: (event: string, listener: (payload: unknown) => void) => unknown;
  off?: (event: string, listener: (payload: unknown) => void) => unknown;
}

export interface TabEventSubscription<E extends string> {
  /** The service path the events are emitted on, e.g. `data-mapper-explorer-profiler`. */
  path: string;
  /** The event names to listen for on that path. */
  events: readonly E[];
  /** This tab's id (`DataMapperViewStore.tabId`): events for any other tab are dropped. */
  tabId: string;
  /** Called with the event's name and payload, for this tab's events only. */
  onEvent: (event: E, payload: DataMapperTabEventPayload) => void;
}

/** Is this payload for this tab? A payload without a tab id is never this tab's. */
export const isForTab = (payload: unknown, tabId: string): payload is DataMapperTabEventPayload =>
  payload !== null && typeof payload === "object" && sameId((payload as { tabId?: unknown }).tabId, tabId);

/**
 * Listen for `events` on `path`, keeping only this tab's. Returns an
 * idempotent disposer that removes exactly the listeners it added. With no
 * Feathers client (a test root, a REST-only build) it subscribes to nothing and
 * the disposer is a no-op: the screens still read each job's `status` on entry.
 */
export function subscribeTabEvents<E extends string>(
  rootStore: RootStore,
  { path, events, tabId, onEvent }: TabEventSubscription<E>
): () => void {
  const app = (rootStore as unknown as { app?: { service?: (p: string) => EventSource | null } }).app;
  const service = typeof app?.service === "function" ? app.service(path) : null;
  if (!service || typeof service.on !== "function") return () => undefined;

  const wired: Array<[E, (payload: unknown) => void]> = events.map((event) => [
    event,
    (payload: unknown) => {
      if (!isForTab(payload, tabId)) return;
      onEvent(event, payload);
    },
  ]);
  for (const [event, listener] of wired) service.on(event, listener);

  let disposed = false;
  return () => {
    if (disposed) return;
    disposed = true;
    for (const [event, listener] of wired) {
      if (typeof service.removeListener === "function") service.removeListener(event, listener);
      else service.off?.(event, listener);
    }
  };
}
